<template>
  <div class="copilot-suggestion mt-1">
    <!-- Botão de disparo (sob demanda) — oculto quando o disparo é feito pelo
         botão único do topo da tela (hideTrigger). -->
    <b-button
      v-if="!recommendation && !hideTrigger"
      size="sm"
      variant="outline-primary"
      :disabled="loading || disabled"
      @click="manualGenerate"
    >
      <b-spinner
        v-if="loading"
        small
        class="mr-50"
      />
      <feather-icon
        v-else
        icon="ZapIcon"
        class="mr-50"
      />
      {{ loading ? "Gerando sugestão..." : "Gerar sugestão de IA" }}
    </b-button>

    <!-- Dica quando o disparo está travado (ex.: metodologia não selecionada) -->
    <small
      v-if="!recommendation && !hideTrigger && disabled && disabledHint"
      class="text-muted d-block mt-50"
    >
      {{ disabledHint }}
    </small>

    <!-- Estado de carregamento por campo (quando disparado pelo botão do topo) -->
    <div
      v-if="hideTrigger && loading && !recommendation"
      class="d-flex align-items-center mt-1"
    >
      <b-spinner
        small
        class="mr-50 text-primary"
      />
      <small class="text-primary">Gerando sugestão de IA...</small>
    </div>

    <!-- Card da sugestão apresentada -->
    <b-card
      v-if="recommendation"
      no-body
      class="copilot-card border mt-1"
    >
      <div class="copilot-card-header d-flex align-items-center px-1 pt-1">
        <feather-icon
          icon="ZapIcon"
          class="text-primary mr-50"
        />
        <small class="font-weight-bold text-primary">Sugestão de IA (apoio — revise antes de aplicar)</small>
      </div>

      <div class="px-1 pb-1">
        <!-- Modo leitura -->
        <p
          v-if="!editing"
          class="copilot-content mb-1 mt-1"
        >
          {{ formatDisplay ? formatDisplay(recommendation.original_content) : recommendation.original_content }}
        </p>

        <!-- Modo edição (altura dinâmica: cresce com o conteúdo) -->
        <b-form-textarea
          v-else
          v-model="editedContent"
          :rows="2"
          max-rows="20"
          no-resize
          class="mb-1 mt-1"
        />

        <!-- Campo de motivo da rejeição -->
        <b-form-input
          v-if="rejecting"
          v-model="rejectionReason"
          placeholder="Motivo da rejeição (opcional)"
          class="mb-1"
        />

        <div
          class="d-flex flex-wrap"
          style="gap: 0.5rem"
        >
          <template v-if="!editing && !rejecting">
            <b-button
              size="sm"
              variant="success"
              :disabled="busy"
              @click="accept"
            >
              Aceitar
            </b-button>
            <b-button
              size="sm"
              variant="outline-primary"
              :disabled="busy"
              @click="startEdit"
            >
              Aceitar e editar
            </b-button>
            <b-button
              size="sm"
              variant="outline-danger"
              :disabled="busy"
              @click="rejecting = true"
            >
              Rejeitar
            </b-button>
            <b-button
              size="sm"
              variant="outline-secondary"
              :disabled="busy || disabled"
              @click="regenerate"
            >
              <b-spinner
                v-if="busy"
                small
              />
              <span v-else>Nova sugestão</span>
            </b-button>
          </template>

          <template v-else-if="editing">
            <b-button
              size="sm"
              variant="success"
              :disabled="busy"
              @click="confirmEdit"
            >
              Aplicar edição
            </b-button>
            <b-button
              size="sm"
              variant="flat-secondary"
              :disabled="busy"
              @click="cancelEdit"
            >
              Cancelar
            </b-button>
          </template>

          <template v-else-if="rejecting">
            <b-button
              size="sm"
              variant="danger"
              :disabled="busy"
              @click="reject"
            >
              Confirmar rejeição
            </b-button>
            <b-button
              size="sm"
              variant="flat-secondary"
              :disabled="busy"
              @click="rejecting = false"
            >
              Cancelar
            </b-button>
          </template>
        </div>
      </div>
    </b-card>
  </div>
</template>

<script>
import {
  BButton,
  BCard,
  BFormTextarea,
  BFormInput,
  BSpinner,
} from 'bootstrap-vue'
import { mapActions } from 'vuex'
import { useToast } from 'vue-toastification/composition'
import ToastificationContent from '@core/components/toastification/ToastificationContent'
import * as types from '../store/types'

export default {
  name: 'CopilotSuggestion',
  components: {
    BButton,
    BCard,
    BFormTextarea,
    BFormInput,
    BSpinner,
  },
  props: {
    // Cliente / plano financeiro alvo, identificado por customers.token.
    customerToken: {
      type: String,
      required: true,
    },
    // Chave do campo no contrato do Copiloto (ver StepsAgentsClient::FIELD_REGISTRY).
    fieldKey: {
      type: String,
      required: true,
    },
    // Quando true, esconde o botão próprio de disparo — a geração é acionada
    // pelo botão único no topo da tela, que chama generate() via ref.
    hideTrigger: {
      type: Boolean,
      default: false,
    },
    // Contexto extra enviado ao agente (ex.: metodologia escolhida no Passo 7).
    fieldText: {
      type: String,
      default: null,
    },
    // Trava o disparo (ex.: só gerar depois de escolher a metodologia).
    disabled: {
      type: Boolean,
      default: false,
    },
    // Mensagem exibida quando o disparo está travado (opcional).
    disabledHint: {
      type: String,
      default: '',
    },

    formatDisplay: {
      type: Function,
      default: null,
    },
  },
  setup() {
    return { toast: useToast() }
  },
  data() {
    return {
      loading: false,
      busy: false,
      recommendation: undefined,
      editing: false,
      editedContent: '',
      rejecting: false,
      rejectionReason: '',
    }
  },
  methods: {
    ...mapActions({
      generateSuggestion: types.GENERATE_COPILOT_SUGGESTION,
      decideSuggestion: types.DECIDE_COPILOT_SUGGESTION,
    }),
    notifyError(text) {
      this.toast({
        component: ToastificationContent,
        props: {
          title: 'Oops!', text, icon: 'AlertTriangleIcon', variant: 'danger',
        },
      })
    },
    // Disparo pelo botão individual do campo. Avisa o pai no início e no fim
    // (para ele desabilitar o botão único do topo apenas enquanto gera).
    manualGenerate() {
      this.$emit('manual-generate')
      return this.generate().finally(() => {
        this.$emit('manual-generate-done')
      })
    },
    generate() {
      // Evita duplicar chamada: pula se já está gerando, já tem sugestão ou está travado.
      if (this.loading || this.recommendation || this.disabled) return Promise.resolve()
      this.loading = true
      return this.generateSuggestion({
        customer_token: this.customerToken,
        field_key: this.fieldKey,
        field_text: this.fieldText,
      })
        .then(resp => {
          this.recommendation = resp.data
        })
        .catch(err => {
          // Fallback: IA indisponível não trava o passo — o PN segue manualmente.
          const unavailable = err?.response?.status === 503
          this.notifyError(
            unavailable
              ? 'O Copiloto está indisponível agora. Preencha o campo manualmente.'
              : 'Não foi possível gerar a sugestão de IA. Tente novamente.',
          )
        })
        .finally(() => {
          this.loading = false
        })
    },
    decide(action, extra = {}) {
      this.busy = true
      return this.decideSuggestion({
        recommendation_id: this.recommendation.id,
        action,
        ...extra,
      })
        .then(resp => Promise.resolve(resp))
        .catch(err => {
          this.notifyError('Não foi possível registrar a decisão. Tente novamente.')
          return Promise.reject(err)
        })
        .finally(() => {
          this.busy = false
        })
    },
    accept() {
      const content = this.recommendation.original_content
      this.decide('accept').then(() => {
        this.$emit('apply', content)
        this.reset()
      })
    },
    startEdit() {
      this.editedContent = this.recommendation.original_content
      this.editing = true
    },
    confirmEdit() {
      const content = this.editedContent
      this.decide('accept_edited', { final_content: content }).then(() => {
        this.$emit('apply', content)
        this.reset()
      })
    },
    cancelEdit() {
      this.editing = false
      this.editedContent = ''
    },
    reject() {
      this.decide('reject', { rejection_reason: this.rejectionReason }).then(() => {
        this.reset()
      })
    },
    regenerate() {
      // Envia o field_text atual (ex.: metodologia selecionada) para a regeneração
      // refletir mudanças feitas depois da primeira sugestão.
      this.decide('regenerate', { field_text: this.fieldText }).then(resp => {
        this.recommendation = resp.data
        this.editing = false
        this.rejecting = false
      })
    },
    reset() {
      this.recommendation = undefined
      this.editing = false
      this.editedContent = ''
      this.rejecting = false
      this.rejectionReason = ''
    },
  },
}
</script>

<style lang="scss" scoped>
.copilot-card {
  background-color: rgba(115, 103, 240, 0.04);
  /* Neutraliza o `min-height: 650px` global de `.financial-planning-menu .card`
     que estava deixando este card com altura fixa enorme. */
  min-height: 0 !important;
  height: auto;
}
.copilot-content {
  white-space: pre-wrap;
  font-size: 0.95rem;
}
</style>
